import { Props } from '@service-victoria/component-library';
import { AxiosResponse } from 'axios';
import { isEmpty } from 'lodash-es';

import { Store } from 'app/interfaces';
import { api, storage, constants } from 'app/utils';
import { v4 as uuid } from 'uuid';

/**
 * API Response for retrieving list of addresses matching search string
 */
type GetAddressResponse = {
  /**
   * Number of records in response
   */
  totalRecords: number;
  /**
   * List of all matching address records
   */
  records: Props.AddressLookup.Interfaces.ApiShortAddress[];
};

const recordIncludesStates = (addressLine: string, states: string[]) => {
  // Pad state with spaces on either side to ensure that state appears as a whole word in result
  // This is to prevent other parts of the address (eg. suburb, street name) creating a false match with this filter
  // eg. if the suburb is MOUNT PLEASANT, this will match the "SA" filter despite being in WA.
  return states.some(value => addressLine.includes(` ${value} `));
};

export default class AddressService {
  static searchUri = '/adapter/v1/cwp/address/addresses';

  private adapter: api.Adapter;
  private addressLookupPromise?: () => Promise<AxiosResponse<GetAddressResponse>>;
  private addressLookupTimeout?: any;

  constructor(baseURL: string, transactionType: string) {
    const currentState = storage.getStorage<Store.State>(constants.ProviderStorageKeys.State);
    if (!currentState.transactions[transactionType]) {
      currentState.transactions[transactionType] = { ID: uuid() };
    }

    this.adapter = new api.Adapter({
      baseURL,
      responseType: 'json',
      headers: {
        'X-TransactionID': currentState.transactions[transactionType].ID,
        timeout: 60 * 10 * 1000,
        'content-type': 'application/json',
        accept: 'application/json'
      }
    });

    this.getAddressDetails = this.getAddressDetails.bind(this);
    this.getAddressesLookup = this.getAddressesLookup.bind(this);
  }

  /**
   * Retrieve a list of addresses
   * @param queryString string to search
   */
  async getAddressesLookup(queryString: string): Promise<Props.AutoComplete.OptionType[]> {
    try {
      const response = await this.adapter.getValue<GetAddressResponse>(queryString);
      return response.data.records
        .filter(record => !isEmpty(record.id))
        .filter(record => recordIncludesStates(record.addressLine, constants.ALLOWED_STATES))
        .map(record => {
          return {
            label: record.addressLine,
            value: record.id
          };
        });
    } catch (e) {
      return [];
    }
  }

  /**
   * Retrieve the full details of the address
   * @param id unique id for the address
   */
  async getAddressDetails(id: string): Promise<Props.AddressLookup.Interfaces.ApiFullAddress> {
    const url = `${AddressService.searchUri}/${encodeURIComponent(id)}`;
    const response = await this.adapter.getValue<Props.AddressLookup.Interfaces.ApiFullAddress>(
      url
    );
    return response.data;
  }
}
